import {ABTEST_STATUS, HOSTS_FEEDS, REVIEW_STATUS, REVIEW_TYPES, USER_ROLES} from '@/api/interfaces';
import {ABTEST_IMG_POSITION} from '@/queries/abTests/interfaces';
import {IUserData} from '@/queries/users/interfaces';

export const WP_PRELOADED = window?.wpApiSettings?.preloaded;
export const WP_USER = WP_PRELOADED?.['/sm_pc/v1/users/me?_embed=hosts']?.body as IUserData;
export const WP_INITIAL = WP_PRELOADED?.['/sm_pc/v1/settings']?.body;
export const WP_SETTINGS = WP_PRELOADED?.['/wp/v2/settings']?.body;

export const WP_SERVER = WP_INITIAL?.serverSettings;
export const WP_LINKS = WP_INITIAL?.systemUrls;
export const WP_UPLOAD = WP_INITIAL?.media?.upload;
export const WP_SENTRY = WP_INITIAL?.sentrySettings?.browserSettings;

export const API_ROOT_URL = WP_SERVER?.root || window.location.origin + '/wp-json/';
export const API_NAMESPACE = WP_SERVER?.appNamespace || 'sm_pc';
export const API_NONCE = WP_SERVER?.nonce;

export const CAPABILITIES = WP_USER?.capabilities;
export const ROLE = WP_USER?.role;

export const IS_ADMIN = ROLE === USER_ROLES.admin;
export const IS_PARTNER = ROLE === USER_ROLES.partner;
export const IS_NON_VERIFIED = ROLE === USER_ROLES.nonVerified;
export const IS_PUBLISHER = ROLE === USER_ROLES.publisher;

export const APP_TITLE = WP_SETTINGS?.title || 'Related Ads';
export const BASE_URL = '/dashboard';

export const APP_SCREENS = {
    onboarding: {slug: '/apply', title: 'Thank you for joining the Related Ads!'},
    nonVerified: {slug: '/', title: 'Almost there'},
    overview: {slug: '/', title: 'Overview'},
    overviewUser: {slug: '/overview-user', title: 'Overview'},
    monitoring: {slug: '/monitoring', title: 'Performance monitoring'},
    articleLibrary: {slug: IS_PUBLISHER ? '/' : '/article-library', title: 'Article library'},
    articleGeneration: {slug: '/article-generation', title: 'Article generation'},
    creativeUploader: {slug: '/creative-uploader', title: 'Creative uploader'},
    creativeLibrary: {slug: '/creative-library', title: 'Creative library'},
    approvalRequests: {slug: '/approval-requests', title: 'Approval requests'},
    abTest: {slug: '/abtest', title: 'A/B tests'},
    addAbTest: {slug: '/add-abtest', title: 'Add A/B test'},
    editAbTest: {slug: '/edit-abtest', title: 'Edit A/B test'},
    userManagement: {slug: '/user-management', title: 'User management'},
    editUser: {slug: '/edit-user', title: 'Edit user'},
    hostManagement: {slug: '/host-management', title: 'Host management'},
    editHost: {slug: '/edit-host', title: 'Edit host'},
    accountSettings: {slug: '/account-settings', title: 'Account settings'},
    contact: {slug: '/contact', title: 'Contact'},
    faq: {slug: '/faq', title: 'FAQ'},
    notFound: {slug: '*', title: 'Page not found'}
};

export const SCREENS_WITHOUT_HEADER = [APP_SCREENS.onboarding.slug].map(slug => slug.replaceAll('/', ''));
export const SCREENS_WITHOUT_SIDEBAR = [APP_SCREENS.onboarding.slug].map(slug => slug.replaceAll('/', ''));

export const QUERY_KEYS = {
    users: {
        currentUser: 'currentUser',
        usersManagement: 'usersManagement',
        usersAutoComplete: 'usersAutoComplete',
        userData: 'userData',
        userHosts: 'userHosts'
    },
    hosts: {
        hostData: 'hostData',
        registeredHosts: 'registeredHosts',
        registeredHostsGrid: 'registeredHostsGrid',
        registeredHostUsers: 'registeredHostUsers'
    },
    creatives: {
        creativeLibrary: 'creativeLibrary',
        creativeLabels: 'creativeLabels'
    },
    articles: {
        article: 'article',
        articleLibrary: 'articleLibrary',
        articleList: 'articleList',
        articleGeneration: 'articleGeneration',
        articlePrompts: 'articlePrompts',
        articleLabels: 'articleLabels',
        articleVerticals: 'articleVerticals',
        approvalRequests: 'approvalRequests',
        overviewApproval: 'overviewApproval'
    },
    monitoring: {
        landingQueries: 'landingQueries',
        overviewStatistics: 'overviewStatistics',
        monitoringArticles: 'monitoringArticles',
        monitoringKeywords: 'monitoringKeywords',
        monitoringFilters: 'monitoringFilters',
        monitoringCharts: 'monitoringCharts',
        monitoringTotals: 'monitoringTotals',
        trafficByPartner: 'trafficByPartner',
        trafficBySource: 'trafficBySource',
        trafficByDomain: 'trafficByDomain',
        trafficByGeo: 'trafficByGeo',
        profitabilityReport: 'profitabilityReport',
        profitabilityChart: 'profitabilityChart'
    },
    help: {
        faq: 'faq',
        contact: 'contact'
    },
    abTests: {
        abTestsGrid: 'abTestsGrid',
        abTestData: 'abTestData'
    },
    wp: {
        mediaItem: 'mediaItem'
    }
};

export const REVENUE_SHARE = 0.9;

export const MEDIA = {
    mobileSm: 480,
    mobile: 576,
    mobileLg: 768,
    tabletSm: 992,
    tablet: 1200,
    laptopSm: 1280,
    laptop: 1440,
    laptopLg: 1512,
    desktopSm: 1600,
    desktop: 1920,
    desktopLg: 2560
};

export const STORAGE_KEYS = {
    newUserFormState: 'newUserFormState',
    publishArticleAuthors: 'publishArticleAuthors'
};

export const APP_COLORS = {
    white: '#ffffff',
    black: '#000000',
    main: '#298afb',
    cta: '#10985f',
    darkBg: '#161823',
    mainBg: '#1d1f2b',
    secondaryBg: '#25293f',
    shade1: '#949bbd',
    shade2: '#6a6f87',
    shade3: '#474b5e',
    shade4: '#363a4f',
    red: '#ff6060',
    green: '#4cf07a',
    yellow: '#ffaa60',
    purple: '#954dab',
    ctaHover: '#17bc77',
    mainHover: '#60A9FF',
    darkBgOpacity70: '#16182370'
};

export const APP_ANIMATIONS = {
    fadeInOut: {
        initial: {opacity: 0},
        animate: {opacity: 1},
        exit: {opacity: 0}
    },
    fadeInOutScale: {
        initial: {opacity: 0, scale: 0},
        animate: {opacity: 1, scale: 1},
        exit: {opacity: 0, scale: 0}
    },
    fadeInOutWidth: {
        initial: {opacity: 0, width: 0},
        animate: {opacity: 1, width: 'auto'},
        exit: {opacity: 0, width: 0}
    },
    fadeInOutHeight: {
        initial: {opacity: 0, height: 0},
        animate: {opacity: 1, height: 'auto'},
        exit: {opacity: 0, height: 0}
    },
    popupToggle: {
        initial: {opacity: 0, y: 16},
        animate: {opacity: 1, y: 0},
        exit: {opacity: 0, y: -16}
    }
};

export const FEED_SELECT_OPTIONS = [
    {label: 'Sedo', value: HOSTS_FEEDS.sedo},
    {label: 'Compado', value: HOSTS_FEEDS.compado}
];

export const ROLE_SELECT_OPTIONS = [
    {label: 'Admin', value: USER_ROLES.admin},
    {label: 'Partner (verified)', value: USER_ROLES.partner},
    {label: 'Partner (non-verified)', value: USER_ROLES.nonVerified}
];

export const REVIEWS_TYPE_SELECT_OPTIONS = [
    {label: 'Article', value: REVIEW_TYPES.article},
    {label: 'Creative', value: REVIEW_TYPES.creative}
];

export const REVIEWS_STATUS_SELECT_OPTIONS = [
    {label: 'Pending', value: REVIEW_STATUS.pending},
    {label: 'Approved', value: REVIEW_STATUS.approved},
    {label: 'Rejected', value: REVIEW_STATUS.rejected}
];

export const ABTEST_STATUS_SELECT_OPTIONS = [
    {label: 'Draft', value: ABTEST_STATUS.draft},
    {label: 'Active', value: ABTEST_STATUS.active},
    {label: 'Inactive', value: ABTEST_STATUS.inactive}
];

export const ABTEST_IMG_POSITION_SELECT_OPTIONS = [
    {label: 'Top of the page', value: ABTEST_IMG_POSITION.top},
    {label: 'Bottom of the page', value: ABTEST_IMG_POSITION.bottom}
];

export const ANTD_THEME_CONFIG = {
    theme: {
        token: {
            fontFamily: 'Instrument Sans, sans-serif',
            primaryColor: APP_COLORS.main,
            colorPrimaryBorder: APP_COLORS.secondaryBg,
            colorBorder: APP_COLORS.shade4,
            colorTextHeading: APP_COLORS.white,
            colorText: APP_COLORS.white,
            colorTextDisabled: APP_COLORS.shade2,
            colorTextPlaceholder: APP_COLORS.shade3,
            colorError: APP_COLORS.red,
            colorErrorText: APP_COLORS.red
        },
        components: {
            Table: {
                headerBorderRadius: 12,
                colorBgContainer: APP_COLORS.secondaryBg,
                borderColor: APP_COLORS.shade4,
                headerBg: APP_COLORS.mainBg,
                headerColor: APP_COLORS.shade1,
                headerSortHoverBg: APP_COLORS.darkBg
            },
            Pagination: {
                itemBg: 'transparent',
                itemActiveBg: APP_COLORS.main
            },
            Select: {
                colorPrimary: APP_COLORS.main,
                colorPrimaryHover: APP_COLORS.shade2,
                colorTextPlaceholder: APP_COLORS.shade1,
                colorTextDisabled: APP_COLORS.shade1,
                optionActiveBg: APP_COLORS.shade3,
                optionActiveColor: APP_COLORS.white,
                optionSelectedBg: APP_COLORS.shade4,
                optionSelectedColor: APP_COLORS.main,
                optionSelectedFontWeight: 400,
                optionFontSize: 16,
                optionPadding: '12px',
                optionLineHeight: '24px',
                zIndexPopup: 3
            }
        }
    }
};

export const COUNTRIES_DATA = [
    {name: 'Afghanistan', code: 'AF'},
    {name: 'Åland Islands', code: 'AX'},
    {name: 'Albania', code: 'AL'},
    {name: 'Algeria', code: 'DZ'},
    {name: 'American Samoa', code: 'AS'},
    {name: 'Andorra', code: 'AD'},
    {name: 'Angola', code: 'AO'},
    {name: 'Anguilla', code: 'AI'},
    {name: 'Antarctica', code: 'AQ'},
    {name: 'Antigua and Barbuda', code: 'AG'},
    {name: 'Argentina', code: 'AR'},
    {name: 'Armenia', code: 'AM'},
    {name: 'Aruba', code: 'AW'},
    {name: 'Australia', code: 'AU'},
    {name: 'Austria', code: 'AT'},
    {name: 'Azerbaijan', code: 'AZ'},
    {name: 'Bahamas, The', code: 'BS'},
    {name: 'Bahrain', code: 'BH'},
    {name: 'Bangladesh', code: 'BD'},
    {name: 'Barbados', code: 'BB'},
    {name: 'Belarus', code: 'BY'},
    {name: 'Belgium', code: 'BE'},
    {name: 'Belize', code: 'BZ'},
    {name: 'Benin', code: 'BJ'},
    {name: 'Bermuda', code: 'BM'},
    {name: 'Bhutan', code: 'BT'},
    {name: 'Bolivarian Republic of Venezuela', code: 'VE'},
    {name: 'Bolivia', code: 'BO'},
    {name: 'Bonaire, Sint Eustatius and Saba', code: 'BQ'},
    {name: 'Bosnia and Herzegovina', code: 'BA'},
    {name: 'Botswana', code: 'BW'},
    {name: 'Bouvet Island', code: 'BV'},
    {name: 'Brazil', code: 'BR'},
    {name: 'British Indian Ocean Territory', code: 'IO'},
    {name: 'Brunei', code: 'BN'},
    {name: 'Bulgaria', code: 'BG'},
    {name: 'Burkina Faso', code: 'BF'},
    {name: 'Burundi', code: 'BI'},
    {name: 'Cabo Verde', code: 'CV'},
    {name: 'Cambodia', code: 'KH'},
    {name: 'Cameroon', code: 'CM'},
    {name: 'Canada', code: 'CA'},
    {name: 'Cayman Islands', code: 'KY'},
    {name: 'Central African Republic', code: 'CF'},
    {name: 'Chad', code: 'TD'},
    {name: 'Chile', code: 'CL'},
    {name: 'China', code: 'CN'},
    {name: 'Christmas Island', code: 'CX'},
    {name: 'Cocos (Keeling) Islands', code: 'CC'},
    {name: 'Colombia', code: 'CO'},
    {name: 'Comoros', code: 'KM'},
    {name: 'Congo', code: 'CG'},
    {name: 'Congo (DRC)', code: 'CD'},
    {name: 'Cook Islands', code: 'CK'},
    {name: 'Costa Rica', code: 'CR'},
    {name: "Côte d'Ivoire", code: 'CI'},
    {name: 'Croatia', code: 'HR'},
    {name: 'Cuba', code: 'CU'},
    {name: 'Curaçao', code: 'CW'},
    {name: 'Cyprus', code: 'CY'},
    {name: 'Czech Republic', code: 'CZ'},
    {name: 'Democratic Republic of Timor-Leste', code: 'TL'},
    {name: 'Denmark', code: 'DK'},
    {name: 'Djibouti', code: 'DJ'},
    {name: 'Dominica', code: 'DM'},
    {name: 'Dominican Republic', code: 'DO'},
    {name: 'Ecuador', code: 'EC'},
    {name: 'Egypt', code: 'EG'},
    {name: 'El Salvador', code: 'SV'},
    {name: 'Equatorial Guinea', code: 'GQ'},
    {name: 'Eritrea', code: 'ER'},
    {name: 'Estonia', code: 'EE'},
    {name: 'Ethiopia', code: 'ET'},
    {name: 'Falkland Islands (Islas Malvinas)', code: 'FK'},
    {name: 'Faroe Islands', code: 'FO'},
    {name: 'Fiji Islands', code: 'FJ'},
    {name: 'Finland', code: 'FI'},
    {name: 'France', code: 'FR'},
    {name: 'French Guiana', code: 'GF'},
    {name: 'French Polynesia', code: 'PF'},
    {name: 'French Southern and Antarctic Lands', code: 'TF'},
    {name: 'Gabon', code: 'GA'},
    {name: 'Gambia, The', code: 'GM'},
    {name: 'Georgia', code: 'GE'},
    {name: 'Germany', code: 'DE'},
    {name: 'Ghana', code: 'GH'},
    {name: 'Gibraltar', code: 'GI'},
    {name: 'Greece', code: 'GR'},
    {name: 'Greenland', code: 'GL'},
    {name: 'Grenada', code: 'GD'},
    {name: 'Guadeloupe', code: 'GP'},
    {name: 'Guam', code: 'GU'},
    {name: 'Guatemala', code: 'GT'},
    {name: 'Guernsey', code: 'GG'},
    {name: 'Guinea', code: 'GN'},
    {name: 'Guinea-Bissau', code: 'GW'},
    {name: 'Guyana', code: 'GY'},
    {name: 'Haiti', code: 'HT'},
    {name: 'Heard Island and McDonald Islands', code: 'HM'},
    {name: 'Honduras', code: 'HN'},
    {name: 'Hong Kong SAR', code: 'HK'},
    {name: 'Hungary', code: 'HU'},
    {name: 'Iceland', code: 'IS'},
    {name: 'India', code: 'IN'},
    {name: 'Indonesia', code: 'ID'},
    {name: 'Iran', code: 'IR'},
    {name: 'Iraq', code: 'IQ'},
    {name: 'Ireland', code: 'IE'},
    {name: 'Israel', code: 'IL'},
    {name: 'Italy', code: 'IT'},
    {name: 'Jamaica', code: 'JM'},
    {name: 'Jan Mayen', code: 'SJ'},
    {name: 'Japan', code: 'JP'},
    {name: 'Jersey', code: 'JE'},
    {name: 'Jordan', code: 'JO'},
    {name: 'Kazakhstan', code: 'KZ'},
    {name: 'Kenya', code: 'KE'},
    {name: 'Kiribati', code: 'KI'},
    {name: 'Korea', code: 'KR'},
    {name: 'Kosovo', code: 'XK'},
    {name: 'Kuwait', code: 'KW'},
    {name: 'Kyrgyzstan', code: 'KG'},
    {name: 'Laos', code: 'LA'},
    {name: 'Latvia', code: 'LV'},
    {name: 'Lebanon', code: 'LB'},
    {name: 'Lesotho', code: 'LS'},
    {name: 'Liberia', code: 'LR'},
    {name: 'Libya', code: 'LY'},
    {name: 'Liechtenstein', code: 'LI'},
    {name: 'Lithuania', code: 'LT'},
    {name: 'Luxembourg', code: 'LU'},
    {name: 'Macao SAR', code: 'MO'},
    {name: 'Macedonia, Former Yugoslav Republic of', code: 'MK'},
    {name: 'Madagascar', code: 'MG'},
    {name: 'Malawi', code: 'MW'},
    {name: 'Malaysia', code: 'MY'},
    {name: 'Maldives', code: 'MV'},
    {name: 'Mali', code: 'ML'},
    {name: 'Malta', code: 'MT'},
    {name: 'Man, Isle of', code: 'IM'},
    {name: 'Marshall Islands', code: 'MH'},
    {name: 'Martinique', code: 'MQ'},
    {name: 'Mauritania', code: 'MR'},
    {name: 'Mauritius', code: 'MU'},
    {name: 'Mayotte', code: 'YT'},
    {name: 'Mexico', code: 'MX'},
    {name: 'Micronesia', code: 'FM'},
    {name: 'Moldova', code: 'MD'},
    {name: 'Monaco', code: 'MC'},
    {name: 'Mongolia', code: 'MN'},
    {name: 'Montenegro', code: 'ME'},
    {name: 'Montserrat', code: 'MS'},
    {name: 'Morocco', code: 'MA'},
    {name: 'Mozambique', code: 'MZ'},
    {name: 'Myanmar', code: 'MM'},
    {name: 'Namibia', code: 'NA'},
    {name: 'Nauru', code: 'NR'},
    {name: 'Nepal', code: 'NP'},
    {name: 'Netherlands', code: 'NL'},
    {name: 'New Caledonia', code: 'NC'},
    {name: 'New Zealand', code: 'NZ'},
    {name: 'Nicaragua', code: 'NI'},
    {name: 'Nicaragua', code: 'NI'},
    {name: 'Niger', code: 'NE'},
    {name: 'Nigeria', code: 'NG'},
    {name: 'Niue', code: 'NU'},
    {name: 'Norfolk Island', code: 'NF'},
    {name: 'North Korea', code: 'KP'},
    {name: 'Northern Mariana Islands', code: 'MP'},
    {name: 'Norway', code: 'NO'},
    {name: 'Oman', code: 'OM'},
    {name: 'Pakistan', code: 'PK'},
    {name: 'Palau', code: 'PW'},
    {name: 'Palestinian Authority', code: 'PS'},
    {name: 'Panama', code: 'PA'},
    {name: 'Papua New Guinea', code: 'PG'},
    {name: 'Paraguay', code: 'PY'},
    {name: 'Peru', code: 'PE'},
    {name: 'Philippines', code: 'PH'},
    {name: 'Pitcairn Islands', code: 'PN'},
    {name: 'Poland', code: 'PL'},
    {name: 'Portugal', code: 'PT'},
    {name: 'Puerto Rico', code: 'PR'},
    {name: 'Qatar', code: 'QA'},
    {name: 'Reunion', code: 'RE'},
    {name: 'Romania', code: 'RO'},
    {name: 'Rwanda', code: 'RW'},
    {name: 'Saint Barthélemy', code: 'BL'},
    {name: 'Saint Helena, Ascension and Tristan da Cunha', code: 'SH'},
    {name: 'Saint Kitts and Nevis', code: 'KN'},
    {name: 'Saint Lucia', code: 'LC'},
    {name: 'Saint Martin (French part)', code: 'MF'},
    {name: 'Saint Pierre and Miquelon', code: 'PM'},
    {name: 'Saint Vincent and the Grenadines', code: 'VC'},
    {name: 'Samoa', code: 'WS'},
    {name: 'San Marino', code: 'SM'},
    {name: 'São Tomé and Príncipe', code: 'ST'},
    {name: 'Saudi Arabia', code: 'SA'},
    {name: 'Senegal', code: 'SN'},
    {name: 'Serbia', code: 'RS'},
    {name: 'Seychelles', code: 'SC'},
    {name: 'Sierra Leone', code: 'SL'},
    {name: 'Singapore', code: 'SG'},
    {name: 'Sint Maarten (Dutch part)', code: 'SX'},
    {name: 'Slovakia', code: 'SK'},
    {name: 'Slovenia', code: 'SI'},
    {name: 'Solomon Islands', code: 'SB'},
    {name: 'Somalia', code: 'SO'},
    {name: 'South Africa', code: 'ZA'},
    {name: 'South Georgia and the South Sandwich Islands', code: 'GS'},
    {name: 'South Sudan', code: 'SS'},
    {name: 'Spain', code: 'ES'},
    {name: 'Sri Lanka', code: 'LK'},
    {name: 'Sudan', code: 'SD'},
    {name: 'Suriname', code: 'SR'},
    {name: 'Svalbard', code: 'SJ'},
    {name: 'Swaziland', code: 'SZ'},
    {name: 'Sweden', code: 'SE'},
    {name: 'Switzerland', code: 'CH'},
    {name: 'Syria', code: 'SY'},
    {name: 'Taiwan', code: 'TW'},
    {name: 'Tajikistan', code: 'TJ'},
    {name: 'Tanzania', code: 'TZ'},
    {name: 'Thailand', code: 'TH'},
    {name: 'Togo', code: 'TG'},
    {name: 'Tokelau', code: 'TK'},
    {name: 'Tonga', code: 'TO'},
    {name: 'Trinidad and Tobago', code: 'TT'},
    {name: 'Tunisia', code: 'TN'},
    {name: 'Turkey', code: 'TR'},
    {name: 'Turkmenistan', code: 'TM'},
    {name: 'Turks and Caicos Islands', code: 'TC'},
    {name: 'Tuvalu', code: 'TV'},
    {name: 'U.S. Minor Outlying Islands', code: 'UM'},
    {name: 'Uganda', code: 'UG'},
    {name: 'Ukraine', code: 'UA'},
    {name: 'United Arab Emirates', code: 'AE'},
    {name: 'United Kingdom', code: 'GB'},
    {name: 'United States', code: 'US'},
    {name: 'Uruguay', code: 'UY'},
    {name: 'Uzbekistan', code: 'UZ'},
    {name: 'Vanuatu', code: 'VU'},
    {name: 'Vatican City', code: 'VA'},
    {name: 'Vietnam', code: 'VN'},
    {name: 'Virgin Islands, U.S.', code: 'VI'},
    {name: 'Virgin Islands, British', code: 'VG'},
    {name: 'Wallis and Futuna', code: 'WF'},
    {name: 'Yemen', code: 'YE'},
    {name: 'Zambia', code: 'ZM'},
    {name: 'Zimbabwe', code: 'ZW'}
];

export const AI_SUPPORTED_LANGUAGES = [
    {code: 'sq-AL', label: 'Albanian', flag: '🇦🇱'},
    {code: 'ar-SA', label: 'Arabic', flag: '🇦🇪'},
    {code: 'hy-AM', label: 'Armenian', flag: '🇦🇲'},
    {code: 'awa-IN', label: 'Awadhi', flag: '🇮🇳'},
    {code: 'az-AZ', label: 'Azerbaijani', flag: '🇦🇿'},
    {code: 'eu-ES', label: 'Basque', flag: '🇪🇸'},
    {code: 'bn-BD', label: 'Bengali', flag: '🇧🇩'},
    {code: 'bho-IN', label: 'Bhojpuri', flag: '🇮🇳'},
    {code: 'bs-BA', label: 'Bosnian', flag: '🇧🇦'},
    {code: 'pt-BR', label: 'Brazilian Portuguese', flag: '🇧🇷'},
    {code: 'bg-BG', label: 'Bulgarian', flag: '🇧🇬'},
    {code: 'yue-HK', label: 'Cantonese (Yue)', flag: '🇨🇳'},
    {code: 'ca-ES', label: 'Catalan', flag: '🇪🇸'},
    {code: 'zh-CN', label: 'Mandarin Chinese', flag: '🇨🇳'},
    {code: 'hr-HR', label: 'Croatian', flag: '🇭🇷'},
    {code: 'cs-CZ', label: 'Czech', flag: '🇨🇿'},
    {code: 'da-DK', label: 'Danish', flag: '🇩🇰'},
    {code: 'doi-IN', label: 'Dogri', flag: '🇮🇳'},
    {code: 'nl-NL', label: 'Dutch', flag: '🇳🇱'},
    {code: 'en-US', label: 'English (US)', flag: '🇺🇸'},
    {code: 'en-GB', label: 'English (UK)', flag: '🇬🇧'},
    {code: 'et-EE', label: 'Estonian', flag: '🇪🇪'},
    {code: 'fo-FO', label: 'Faroese', flag: '🇫🇴'},
    {code: 'fi-FI', label: 'Finnish', flag: '🇫🇮'},
    {code: 'fr-FR', label: 'French', flag: '🇫🇷'},
    {code: 'gl-ES', label: 'Galician', flag: '🇪🇸'},
    {code: 'ka-GE', label: 'Georgian', flag: '🇬🇪'},
    {code: 'de-DE', label: 'German', flag: '🇩🇪'},
    {code: 'el-GR', label: 'Greek', flag: '🇬🇷'},
    {code: 'gu-IN', label: 'Gujarati', flag: '🇮🇳'},
    {code: 'hi-IN', label: 'Hindi', flag: '🇮🇳'},
    {code: 'hu-HU', label: 'Hungarian', flag: '🇭🇺'},
    {code: 'id-ID', label: 'Indonesian', flag: '🇮🇩'},
    {code: 'ga-IE', label: 'Irish', flag: '🇮🇪'},
    {code: 'it-IT', label: 'Italian', flag: '🇮🇹'},
    {code: 'ja-JP', label: 'Japanese', flag: '🇯🇵'},
    {code: 'jv-ID', label: 'Javanese', flag: '🇮🇩'},
    {code: 'kn-IN', label: 'Kannada', flag: '🇮🇳'},
    {code: 'ks-IN', label: 'Kashmiri', flag: '🇮🇳'},
    {code: 'kk-KZ', label: 'Kazakh', flag: '🇰🇿'},
    {code: 'kok-IN', label: 'Konkani', flag: '🇮🇳'},
    {code: 'ko-KR', label: 'Korean', flag: '🇰🇷'},
    {code: 'ky-KG', label: 'Kyrgyz', flag: '🇰🇬'},
    {code: 'lv-LV', label: 'Latvian', flag: '🇱🇻'},
    {code: 'lt-LT', label: 'Lithuanian', flag: '🇱🇹'},
    {code: 'mk-MK', label: 'Macedonian', flag: '🇲🇰'},
    {code: 'mai-IN', label: 'Maithili', flag: '🇮🇳'},
    {code: 'ms-MY', label: 'Malay', flag: '🇲🇾'},
    {code: 'mt-MT', label: 'Maltese', flag: '🇲🇹'},
    {code: 'mr-IN', label: 'Marathi', flag: '🇮🇳'},
    {code: 'ne-NP', label: 'Nepali', flag: '🇳🇵'},
    {code: 'nb-NO', label: 'Norwegian', flag: '🇳🇴'},
    {code: 'or-IN', label: 'Oriya (Odia)', flag: '🇮🇳'},
    {code: 'fa-IR', label: 'Persian (Farsi)', flag: '🇮🇷'},
    {code: 'pl-PL', label: 'Polish', flag: '🇵🇱'},
    {code: 'pt-PT', label: 'Portuguese', flag: '🇵🇹'},
    {code: 'pa-IN', label: 'Punjabi', flag: '🇮🇳'},
    {code: 'ro-RO', label: 'Romanian', flag: '🇷🇴'},
    {code: 'sa-IN', label: 'Sanskrit', flag: '🇮🇳'},
    {code: 'sr-RS', label: 'Serbian', flag: '🇷🇸'},
    {code: 'si-LK', label: 'Sinhala', flag: '🇱🇰'},
    {code: 'sk-SK', label: 'Slovak', flag: '🇸🇰'},
    {code: 'sl-SI', label: 'Slovene', flag: '🇸🇮'},
    {code: 'es-ES', label: 'Spanish', flag: '🇪🇸'},
    {code: 'uk-UA', label: 'Ukrainian', flag: '🇺🇦'},
    {code: 'ur-PK', label: 'Urdu', flag: '🇵🇰'},
    {code: 'uz-UZ', label: 'Uzbek', flag: '🇺🇿'},
    {code: 'vi-VN', label: 'Vietnamese', flag: '🇻🇳'},
    {code: 'cy-GB', label: 'Welsh', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿'},
    {code: 'zh-HK', label: 'Wu (Shanghainese)', flag: '🇨🇳'}
];

export const SPECIAL_SYMBOLS_MAP: Record<string, string> = {
    '!': '__EXCLAMATION__',
    '@': '__AT__',
    '#': '__HASH__',
    $: '__DOLLAR__',
    '%': '__PERCENT__',
    '^': '__CARET__',
    '&': '__AMPERSAND__',
    '*': '__ASTERISK__',
    '(': '__LPAREN__',
    ')': '__RPAREN__',
    '-': '__HYPHEN__',
    _: '__UNDERSCORE__',
    '=': '__EQUAL__',
    '+': '__PLUS__',
    '[': '__LBRACKET__',
    ']': '__RBRACKET__',
    '{': '__LBRACE__',
    '}': '__RBRACE__',
    '\\': '__BACKSLASH__',
    '|': '__PIPE__',
    ';': '__SEMICOLON__',
    ':': '__COLON__',
    "'": '__SQUOTE__',
    '"': '__DQUOTE__',
    ',': '__COMMA__',
    '.': '__PERIOD__',
    '<': '__LT__',
    '>': '__GT__',
    '/': '__SLASH__',
    '?': '__QUESTION__',
    ' ': '__SPACE__'
};

export const TRANSLITERATE_CYRILLIC_MAP: Record<string, string> = {
    А: 'A',
    а: 'a',
    Б: 'B',
    б: 'b',
    В: 'V',
    в: 'v',
    Г: 'G',
    г: 'g',
    Д: 'D',
    д: 'd',
    Е: 'E',
    е: 'e',
    Ё: 'Yo',
    ё: 'yo',
    Ж: 'Zh',
    ж: 'zh',
    З: 'Z',
    з: 'z',
    И: 'I',
    и: 'i',
    Й: 'Y',
    й: 'y',
    К: 'K',
    к: 'k',
    Л: 'L',
    л: 'l',
    М: 'M',
    м: 'm',
    Н: 'N',
    н: 'n',
    О: 'O',
    о: 'o',
    П: 'P',
    п: 'p',
    Р: 'R',
    р: 'r',
    С: 'S',
    с: 's',
    Т: 'T',
    т: 't',
    У: 'U',
    у: 'u',
    Ф: 'F',
    ф: 'f',
    Х: 'Kh',
    х: 'kh',
    Ц: 'Ts',
    ц: 'ts',
    Ч: 'Ch',
    ч: 'ch',
    Ш: 'Sh',
    ш: 'sh',
    Щ: 'Shch',
    щ: 'shch',
    Ъ: '',
    ъ: '',
    Ы: 'Y',
    ы: 'y',
    Ь: '',
    ь: '',
    Э: 'E',
    э: 'e',
    Ю: 'Yu',
    ю: 'yu',
    Я: 'Ya',
    я: 'ya'
};
